import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';
import { getRoleLabel, getRoleColor } from '../services/mockUsers';
import { Project } from '../services/mockProjects';

const ProjectParticipants: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin, isAgence } = usePermissions();
  const [project, setProject] = useState<Project | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      const response = await api.projects.getById(id);
      if (response.project) {
        const p = response.project as any;
        setProject({
          id: p.id,
          title: p.title,
          description: p.description || '',
          status: p.status || 'pending',
          priority: p.priority || 'medium',
          created_by: p.created_by,
          created_by_name: p.created_by_name || '',
          manager_agence: p.manager_agence,
          manager_agence_name: p.manager_agence_name || '',
          contact_soebra: p.contact_soebra,
          contact_soebra_name: p.contact_soebra_name || '',
          assigned_to: p.assigned_to,
          assigned_to_name: p.assigned_to_name || '',
          created_at: p.created_at || new Date().toISOString(),
          updated_at: p.updated_at || new Date().toISOString(),
        } as Project);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du projet:', error);
    }
  };

  const getParticipants = () => {
    if (!project) return [];
    
    const participants = [
      { id: project.created_by, name: project.created_by_name, role: 'agence' },
      project.manager_agence ? { id: project.manager_agence, name: project.manager_agence_name, role: 'agence' } : null,
      project.contact_soebra ? { id: project.contact_soebra, name: project.contact_soebra_name, role: 'soebra' } : null,
      project.assigned_to ? { id: project.assigned_to, name: project.assigned_to_name, role: 'agent_terrain' } : null,
    ].filter(Boolean) as Array<{ id: string; name?: string; role: string }>;

    // Charger les utilisateurs depuis l'API
    return Promise.all(participants.map(async (p) => {
      try {
        const { users } = await api.users.getAll();
        const user = users.find((u: any) => u.id === p.id);
        return {
          id: p.id,
          name: p.name || user?.name || 'Inconnu',
          role: user?.role || p.role,
          organization: user?.organization,
        };
      } catch (error) {
        return {
          id: p.id,
          name: p.name || 'Inconnu',
          role: p.role,
        };
      }
    }));
  };

  const handleAddParticipant = () => {
    if (!selectedUserId || !project) return;

    // En production, appeler l'API pour ajouter le participant
    alert(`Participant ajouté (simulation). En production, cela mettrait à jour le projet.`);
    setOpenAddDialog(false);
    setSelectedUserId('');
  };

  const handleRemoveParticipant = (userId: string) => {
    if (!project) return;
    
    // En production, appeler l'API pour retirer le participant
    alert(`Participant retiré (simulation). En production, cela mettrait à jour le projet.`);
  };

  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [participants, setParticipants] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  
  useEffect(() => {
    const loadParticipants = async () => {
      if (!project) return;
      try {
        const loadedParticipants = await getParticipants();
        setParticipants(loadedParticipants);
      } catch (error) {
        console.error('Erreur lors du chargement des participants:', error);
        setParticipants([]);
      }
    };
    loadParticipants();
  }, [project]);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { users: usersList } = await api.users.getAll();
        setAllUsers(usersList);
        setAvailableUsers(usersList.filter((u: any) => !participants.some((p: any) => p.id === u.id)));
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      }
    };
    if (project) {
      loadUsers();
    }
  }, [project, participants]);

  if (!project) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')}>
          Retour
        </Button>
        <Typography variant="h6" sx={{ mt: 2 }}>
          Projet non trouvé
        </Typography>
      </Box>
    );
  }

  if (!isAdmin && !isAgence) {
    return (
      <Box>
        <Alert severity="error">
          Vous n'avez pas les permissions nécessaires pour gérer les participants du projet.
        </Alert>
      </Box>
    );
  }


  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(`/projects/${project.id}`)}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ ml: 2 }}>
          <Typography variant="h4">{project.title}</Typography>
          <Typography variant="body2" color="text.secondary">
            Gestion des participants
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">Participants du Projet</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
            disabled={availableUsers.length === 0}
          >
            Ajouter un participant
          </Button>
        </Box>

        {participants.length === 0 ? (
          <Alert severity="info">Aucun participant assigné à ce projet</Alert>
        ) : (
          <List>
            {participants.map((participant, index) => {
              const user = allUsers.find((u: any) => u.id === participant.id);
              return (
                <React.Fragment key={participant.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        {participant.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {participant.name}
                          </Typography>
                          <Chip
                            label={getRoleLabel(participant.role)}
                            size="small"
                            sx={{
                              bgcolor: getRoleColor(participant.role),
                              color: 'white',
                            }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {user?.email || 'Email non disponible'}
                          </Typography>
                          {participant.organization && (
                            <Typography variant="caption" color="text.secondary">
                              {participant.organization}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveParticipant(participant.id)}
                      disabled={
                        participant.id === project.created_by ||
                        (participant.id === project.manager_agence && !isAdmin)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItem>
                  {index < participants.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Paper>

      {/* Dialog Ajout Participant */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajouter un participant</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Utilisateur</InputLabel>
            <Select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              {availableUsers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 24, height: 24 }}>
                      {user.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="body2">{user.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getRoleLabel(user.role)} - {user.email}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {availableUsers.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Tous les utilisateurs sont déjà participants à ce projet.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Annuler</Button>
          <Button
            onClick={handleAddParticipant}
            variant="contained"
            disabled={!selectedUserId}
          >
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectParticipants;



